import { countryConfig } from '../country';

/**
 * An Uzbek ИНН is exactly nine digits, and only such a value has a profile page.
 *
 * ⚠️ Why this exists: 635 award rows carry a `winner_tin` that is NOT nine digits
 * — 317 distinct values including `DSO-FZCO-35154`, `115`, `99`, and 12
 * `tenders.winner_inn` including `0000000`. Every one of them was rendered as a
 * link to `/supplier/{tin}`, a page that cannot exist. A paying user clicked a
 * supplier name and got nothing.
 *
 * ⚠️ These identifiers are NOT junk to be discarded. They are the real upstream
 * registration numbers of (mostly foreign) suppliers, and the only handle we
 * have on those companies. Keep showing the name and the id — just do not
 * pretend there is a page behind it.
 */
export function isLinkableTin(
  tin: string | null | undefined,
  country: string = countryConfig.code,
): boolean {
  return typeof tin === 'string' && (TIN_RE[country] ?? TIN_RE.uz).test(tin.trim());
}

/**
 * The registry id each deployment has profile pages for. KZ keys companies on the
 * 12-digit БИН/ИИН, and `/buyer/:tin` + `/supplier/:tin` already serve them there
 * (verified 2026-09-28: `/buyer/260641026647` → 200, 45 awards) — the 9-digit rule
 * alone meant no KZ tender page ever linked to its buyer's profile.
 */
const TIN_RE: Record<string, RegExp> = { uz: /^\d{9}$/, kz: /^\d{12}$/ };

/**
 * The schema.org `PropertyValue.propertyID` the company pages put on their
 * registry id: «INN» for the Uzbek ИНН (what they always printed), «BIN» for the
 * Kazakh БИН/ИИН. Machine-readable only — the visible label comes from i18n.
 */
export const ORG_ID_PROPERTY: string = countryConfig.code === 'kz' ? 'BIN' : 'INN';
